/* eslint-disable import/no-extraneous-dependencies */
import './Calendar.scss';

import React from 'react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import classNames from 'classnames';
import dayjs from 'dayjs';

import useWindowDimensions from 'hooks/useWindowDimensions';
import { capitalizeFirstLetter, isThirtyMinutesApart } from 'util';

const renderEventContent = (eventInfo, view) => {
  const start = dayjs(new Date(eventInfo.event.startStr)).utc().format('h:mma');
  const end = dayjs(new Date(eventInfo.event.endStr)).utc().format('h:mma');
  const { title } = eventInfo.event;
  const { animal, groomer } = eventInfo.event.extendedProps.description || {};
  if (view.value === 'Monthly') {
    return (
      <p
        className={classNames(
          'f11 ellipsis pl-5 pointer p-2 w-100 radius-2',
          title === 'Available' ? 'b-available c-black' : 'c-white'
        )}
      >
        {title} {start} - {end}
      </p>
    );
  }

  const groomerName =
    groomer || eventInfo.event.extendedProps.groomer?.firstName;
  return (
    <div className="h-100 flex-column justify-between">
      <div>
        <p className="f11 bold">{title}</p>
        <p className="f11">
          {start} - {end}
        </p>
        {animal && (
          <>
            <p className="f11">{animal.name}</p>
            {animal.breed && <p className="f11">{animal.breed?.value}</p>}
            {animal.size && <p className="f11">{animal.size?.value}</p>}
          </>
        )}
      </div>
      <p className="f11 italic">{capitalizeFirstLetter(groomerName)}</p>
    </div>
  );
};

// See https://fullcalendar.io/docs#toc for API docs
const Calendar = React.forwardRef(
  (
    {
      view,
      events,
      onClickEvent,
      onClickBackground,
      handleDatesChange,
      initialDate,
      slotMinTime,
      slotMaxTime,
    },
    ref
  ) => {
    const { width } = useWindowDimensions();
    const isTablet = width < 1200;
    // Prevent users from dragging/selecting acrosss multiple days
    const selectAllow = ({ startStr, endStr }) => {
      const start = new Date(startStr);
      const end = new Date(endStr);

      if (isTablet && !isThirtyMinutesApart(start, end)) {
        return false;
      }

      // Compare UTC year, month, and day
      return (
        start.getUTCFullYear() === end.getUTCFullYear() &&
        start.getUTCMonth() === end.getUTCMonth() &&
        start.getUTCDate() === end.getUTCDate()
      );
    };

    return (
      <FullCalendar
        ref={ref}
        timeZone="UTC"
        plugins={[timeGridPlugin, dayGridPlugin, interactionPlugin]}
        viewClassNames={[`current-view--${view.value.toLowerCase()}`]}
        initialView={view.id}
        events={events}
        // eventMaxStack={1}
        slotMinTime={slotMinTime}
        slotMaxTime={slotMaxTime}
        slotLabelContent={(slot) => dayjs(slot.date).utc().format('ha')}
        allDaySlot={false}
        selectAllow={selectAllow}
        selectable
        eventContent={(eventInfo) => renderEventContent(eventInfo, view)}
        eventDidMount={(ev) => {
          const { el, event } = ev;
          if (event._def.extendedProps.isAvailability) {
            // Traverse up to find the .fc-timegrid-event-harness container
            const harnessElement = el.closest('.fc-timegrid-event-harness');
            if (harnessElement) {
              harnessElement.classList.add('event-at-back');
            }
          }
        }}
        initialDate={initialDate}
        firstDay={0}
        dayHeaderContent={(args) => {
          if (view.value === 'Weekly') {
            return dayjs(args.date).utc().format('ddd D');
          }
          if (view.value === 'Daily') {
            return dayjs(args.date).utc().format('dddd D');
          }
          if (view.value === 'Monthly') {
            return dayjs(args.date).utc().format('ddd');
          }
        }}
        titleFormat={{ month: 'long', year: 'numeric' }}
        headerToolbar={{
          start: 'prev,next title',
          center: '',
          end: '',
        }}
        eventTimeFormat={{
          hour: 'numeric',
          minute: '2-digit',
          meridiem: true,
          hour12: true,
        }}
        longPressDelay={100}
        select={onClickBackground}
        eventClick={onClickEvent}
        datesSet={handleDatesChange}
      />
    );
  }
);

export default Calendar;
